/**
 * App Config Error / Disclaimer Screen
 *
 * Shown when country config (GET /v1/public/country-by-origin) fails or returns
 * invalid data. Blocks the entire app; user is instructed to contact administrator.
 */

import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { AlertTriangle } from 'lucide-react';

interface AppConfigErrorProps {
  onRetry: () => void;
}

export const AppConfigError: React.FC<AppConfigErrorProps> = ({ onRetry }) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white p-4">
      <Card className="w-full max-w-[600px] animate-in fade-in slide-in-from-bottom-4 border-slate-200 py-0 shadow-sm duration-500">
        <CardHeader className="space-y-4 pb-2 pt-8 text-center">
          <div className="flex justify-center">
            <img
              src="/bls-logo.png"
              alt="BLS International"
              className="h-14 w-auto"
            />
          </div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h1 className="text-lg font-semibold text-zinc-700">
            {t('appConfig.errorTitle')}
          </h1>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          <p className="mb-6 text-center text-sm leading-relaxed text-gray-700">
            {t('appConfig.errorMessage')}
          </p>

          <div className="flex justify-center">
            <Button
              type="button"
              onClick={onRetry}
              className="h-10 w-40 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('appConfig.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppConfigError;
